<template>
  <div>
    <div class="editable" :class="{ disabled: disabled, success: success, error: error }">
      <highlight class="highlight-content" :highlights="highlights" :text="htmlOutput"></highlight>
      <div
        class="contenteditable"
        ref="input"
        :contenteditable="!disabled"
        @input="sync"
        @drop="onDrop"
        placeholder="内容入力"
        spellcheck="false"
      ></div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import Highlight from '@/client/components/_atoms/highlight'

export default defineComponent({
  components: {
    Highlight
  },
  props: {
    value: String,
    highlights: {
      type: Array as PropType<{ type: string; keywords: string[] }[]>,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    success: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      internalValue: '',
      htmlOutput: ''
    }
  },
  mounted() {
    this.internalValue = this.value ?? ''
    this.htmlOutput = this.internalValue
    this.inputElement().innerHTML = this.htmlOutput
  },
  watch: {
    value() {
      const value = this.removeLastNewLine(this.value ?? '')
      if (this.internalValue !== value) {
        this.internalValue = value
        this.htmlOutput = this.internalValue
        this.inputElement().innerHTML = this.htmlOutput
      }
    }
  },
  methods: {
    /**
     * 入力欄の要素
     */
    inputElement(): HTMLElement {
      return this.$refs.input as HTMLElement
    },

    /**
     * 入力イベント
     */
    sync() {
      const input = this.inputElement()
      if (this.internalValue === input.innerText) {
        return
      }
      this.htmlOutput = input.innerHTML
      this.internalValue = this.removeLastNewLine(input.innerText)

      this.$emit('input', this.internalValue)
    },

    /**
     * ドロップイベント
     */
    onDrop(event: any) {
      // ドロップをdisabledにする
      event.preventDefault()
      event.stopPropagation()
    },

    /**
     * 最後改行を削除する
     * @param {string} text テキスト
     * @returns {string} テキスト
     */
    removeLastNewLine(text: string): string {
      return text.replace(/\\n+$/, '')
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.editable {
  position: relative;
  min-height: 180px;
  max-height: 800px;
  padding: $spacing-unit;
  overflow: auto;
  resize: vertical;
  border: 1px solid $border-color;
  border-radius: math.div($spacing-unit, 2);

  &:focus-within {
    border-color: $primary-color;
  }

  &.success:focus-within {
    border-color: $success-color;
  }

  &.error:focus-within {
    border-color: $danger-color;
  }

  &.disabled {
    cursor: not-allowed;
    background-color: rgba($background-color, 0.6);
  }
}

.highlight-content {
  overflow-wrap: break-word;
  white-space: pre-wrap;
}

.contenteditable {
  position: absolute;
  inset: $spacing-unit $spacing-unit $spacing-unit $spacing-unit;
  z-index: 2;
  color: transparent;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  caret-color: black;
  outline: none;

  &[placeholder]:empty::before {
    color: #ccc;
    content: attr(placeholder);
  }
}
</style>
